import React, { useEffect, useRef } from "react";
import "./drawer.css";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui-components/icon";

interface DrawerProps {
    open: boolean;
    title: string;
    closeLabel: string;
    children: React.ReactNode;
    onClose: () => void;
    side?: "start" | "end";
    panelId?: string;
}

const FOCUSABLE = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Drawer({ open, title, closeLabel, children, onClose, side = "start", panelId }: DrawerProps) {
    const isEnd = side === "end";
    const panelClass = isEnd ? "drawer" : "side-panel";
    const headClass = isEnd ? "drawer-head" : "side-panel__head";
    const closeClass = isEnd ? "drawer-close" : "panel-close";
    const panelRef = useRef<HTMLElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;
        if (!open) {
            panel.setAttribute("inert", "");
            return;
        }

        panel.removeAttribute("inert");
        previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        requestAnimationFrame(() => closeRef.current?.focus());

        const keydown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }
            if (event.key !== "Tab") return;
            const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(element => !element.hasAttribute("disabled") && element.offsetParent !== null);
            if (!focusable.length) {
                event.preventDefault();
                panel.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", keydown);
        return () => {
            document.removeEventListener("keydown", keydown);
            panel.setAttribute("inert", "");
            previousFocusRef.current?.focus();
        };
    }, [open, onClose]);

    return createPortal(
        <div className={`drawer-layer${open ? " open" : ""}`} aria-hidden={!open}>
            <button className={`scrim${open ? " open" : ""}`} type="button" aria-label={closeLabel} onClick={onClose} tabIndex={open ? 0 : -1} />
            <aside ref={panelRef} id={panelId} className={`${panelClass}${open ? " open" : ""}`} role="dialog" aria-modal="true" aria-hidden={!open} aria-label={title} tabIndex={-1}>
                <div className={headClass}>
                    <strong>{title}</strong>
                    <button ref={closeRef} className={closeClass} type="button" onClick={onClose} aria-label={closeLabel} tabIndex={open ? 0 : -1}>
                        <Icon name="x" size="sm" />
                    </button>
                </div>
                {children}
            </aside>
        </div>,
        document.body,
    );
}
